import { useState } from 'react';
import { Box, Flex, Stack, Text, Loader, Group, Alert } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconUpload, IconFileText, IconFileImport, IconMarkdown } from '@tabler/icons-react';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TemplateCreationData, TemplateExportData } from 'src/company/templates/types';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { parseTemplateJson } from 'src/company/templates/utils/templateExport';
import { useCreateTemplateFromJson } from 'src/company/templates/hooks/useCreateTemplateFromJson';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useClients } from 'src/clients/hooks/useClients';
import { v4 as uuidv4 } from 'uuid';
import Config from 'src/common/Config';
import {
  TEMPLATE_CREATION_FLOW_SCREEN_IDS,
  TEMPLATE_CREATION_FLOW_STEP_IDS,
} from 'src/company/templates/components/template-creation-flow/templateCreationFlowIds';

type ImportMethod = 'upload' | 'json' | 'markdown' | null;

type ParsedMarkdownSection = {
  id: string;
  template_text: string;
  section_header: string | null;
  section_header_level: number;
  section_instructions: string;
  preserve_as_is: boolean;
  agent: string;
  domain_filter: string[] | null;
};

export default function ImportMethodStep(props: StepProps<TemplateCreationData>) {
  const { data, setData, goToStepById } = props;
  const [selectedMethod, setSelectedMethod] = useState<ImportMethod>(null);
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [parsedJson, setParsedJson] = useState<TemplateExportData | null>(null);
  const [markdownFile, setMarkdownFile] = useState<File | null>(null);
  const [parsedMarkdownSections, setParsedMarkdownSections] = useState<
    ParsedMarkdownSection[] | null
  >(null);
  const [isParsingMarkdown, setIsParsingMarkdown] = useState(false);
  const { createTemplateFromJson, isLoading, progress } = useCreateTemplateFromJson();
  const { clients } = useClients({ isTest: true, companyId: data.company.id });

  const handleMethodSelect = (method: ImportMethod) => {
    setSelectedMethod(method);
    setJsonFile(null);
    setParsedJson(null);
    setMarkdownFile(null);
    setParsedMarkdownSections(null);
  };

  const handleJsonUpload = async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    try {
      const parsed = await parseTemplateJson(file);
      setJsonFile(file);
      setParsedJson(parsed);
    } catch (error) {
      notifications.show({
        title: 'Invalid JSON file',
        message: error instanceof Error ? error.message : 'Failed to parse JSON file',
        color: 'red',
      });
    }
  };

  const handleMarkdownUpload = async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    setIsParsingMarkdown(true);
    try {
      const markdownContent = await file.text();
      const response = await fetch(`${Config.getApiUrl()}/admin/templates/parse-legacy-markdown`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ markdownContent }),
      });

      if (!response.ok) {
        throw new Error('Failed to parse markdown');
      }

      const sections: ParsedMarkdownSection[] = await response.json();
      setMarkdownFile(file);
      setParsedMarkdownSections(sections);
    } catch (error) {
      notifications.show({
        title: 'Invalid markdown file',
        message: error instanceof Error ? error.message : 'Failed to parse markdown file',
        color: 'red',
      });
    } finally {
      setIsParsingMarkdown(false);
    }
  };

  const canGoForward =
    selectedMethod === 'upload' ||
    (selectedMethod === 'json' && parsedJson !== null) ||
    (selectedMethod === 'markdown' && parsedMarkdownSections !== null);

  const handleNext = async () => {
    if (selectedMethod === 'upload') {
      props.nextStep();
      return;
    }

    if (selectedMethod === 'json' && parsedJson) {
      try {
        const previewClientId = clients?.[0]?.id || null;
        const template = await createTemplateFromJson(data.company.id, parsedJson, previewClientId);

        const newData: TemplateCreationData = {
          ...data,
          templateName: parsedJson.templateName,
          templateDescription: parsedJson.templateDescription,
          templateType: parsedJson.templateType,
          sectionSplittingLevel: parsedJson.sectionSplittingLevel,
          templateSections: parsedJson.sections.map(section => ({
            id: uuidv4(),
            sectionHeader: section.sectionHeader,
            sectionHeaderLevel: section.sectionHeaderLevel,
            templatedMarkdown: section.templatedMarkdown,
            populatedMarkdown: section.populatedMarkdown || section.templatedMarkdown,
            sectionInstructions: section.sectionInstructions,
            preserveAsIs: section.preserveAsIs,
            isContentComplete: true,
            isSectionInPopulatedMarkdown: true,
            agent: section.agent,
            domainFilter: section.domainFilter,
          })),
          createdTemplate: template,
          importedFromJson: true,
          previewClientId,
          meetingNoteTense: parsedJson.meetingNoteTense ?? 'THIRD_PERSON',
        };

        setData(newData);
        goToStepById(
          TEMPLATE_CREATION_FLOW_STEP_IDS.editSections,
          TEMPLATE_CREATION_FLOW_SCREEN_IDS.editSectionInstructions,
          newData,
        );
      } catch (error) {
        notifyAndLogError(
          'Failed to create template',
          'An error occurred while creating the template from JSON. Please try again.',
          error,
        );
      }
    }

    if (selectedMethod === 'markdown' && parsedMarkdownSections) {
      try {
        const previewClientId = clients?.[0]?.id || null;

        const markdownExportData: TemplateExportData = {
          version: 1,
          templateType: data.templateType!,
          templateName: data.templateName,
          templateDescription: data.templateDescription,
          sectionSplittingLevel: 2,
          sections: parsedMarkdownSections.map(section => ({
            sectionHeader: section.section_header ?? '',
            sectionHeaderLevel: section.section_header_level,
            templatedMarkdown: section.template_text,
            populatedMarkdown: section.template_text,
            sectionInstructions: section.section_instructions,
            preserveAsIs: section.preserve_as_is,
            agent: section.agent as TemplateExportData['sections'][0]['agent'],
            domainFilter:
              section.domain_filter as TemplateExportData['sections'][0]['domainFilter'],
          })),
          meetingNoteTense: data.meetingNoteTense ?? 'THIRD_PERSON',
        };

        const template = await createTemplateFromJson(
          data.company.id,
          markdownExportData,
          previewClientId,
        );

        const newData: TemplateCreationData = {
          ...data,
          sectionSplittingLevel: 2,
          templateSections: parsedMarkdownSections.map(section => ({
            id: section.id,
            sectionHeader: section.section_header ?? '',
            sectionHeaderLevel: section.section_header_level,
            templatedMarkdown: section.template_text,
            populatedMarkdown: section.template_text,
            sectionInstructions: section.section_instructions,
            preserveAsIs: section.preserve_as_is,
            isContentComplete: true,
            isSectionInPopulatedMarkdown: true,
            agent: section.agent,
            domainFilter: section.domain_filter ?? undefined,
          })),
          createdTemplate: template,
          importedFromJson: true,
          previewClientId,
          meetingNoteTense: data.meetingNoteTense ?? 'THIRD_PERSON',
        };

        setData(newData);
        goToStepById(
          TEMPLATE_CREATION_FLOW_STEP_IDS.editSections,
          TEMPLATE_CREATION_FLOW_SCREEN_IDS.editSectionInstructions,
          newData,
        );
      } catch (error) {
        notifyAndLogError(
          'Failed to create template',
          'An error occurred while creating the template from markdown. Please try again.',
          error,
        );
      }
    }
  };

  const renderMarkdownUpload = () => {
    if (isParsingMarkdown) {
      return (
        <Flex align="center" justify="center" gap="xs" p="md">
          <Loader size="sm" />
          <Text size="sm" c="gray.6">
            Parsing markdown...
          </Text>
        </Flex>
      );
    }

    if (markdownFile && parsedMarkdownSections) {
      return (
        <Box
          p="sm"
          style={{
            borderRadius: '12px',
            backgroundColor: 'var(--mantine-color-gray-1)',
            border: '1px solid var(--mantine-color-green-5)',
          }}
        >
          <Flex align="center" gap="xs">
            <IconFileText size={20} color="var(--mantine-color-green-7)" />
            <Stack gap={0}>
              <Text fw={FONT_WEIGHTS.medium} size="sm" c="gray.10">
                {markdownFile.name}
              </Text>
              <Text fw={FONT_WEIGHTS.regular} size="xs" c="gray.6">
                {parsedMarkdownSections.length} sections found
              </Text>
            </Stack>
          </Flex>
        </Box>
      );
    }

    return (
      <Dropzone
        onDrop={handleMarkdownUpload}
        onReject={fileRejections => {
          fileRejections.forEach(fileRejection => {
            notifications.show({
              title: `Failed to upload ${fileRejection.file.name}`,
              message: fileRejection.errors.map(e => e.message).join('\n'),
              color: 'red',
            });
          });
        }}
        maxSize={10 * 1024 * 1024}
        maxFiles={1}
        accept={['text/markdown', '.md']}
        style={{
          border: '1px dashed var(--mantine-color-gray-4)',
          borderRadius: '12px',
          padding: '16px',
          minHeight: '80px',
          backgroundColor: 'var(--mantine-color-gray-0)',
        }}
      >
        <Flex align="center" justify="center" gap="xs" style={{ minHeight: '48px' }}>
          <IconUpload size={16} color="var(--mantine-color-gray-6)" />
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.6">
            Drop markdown file here or click to browse
          </Text>
        </Flex>
      </Dropzone>
    );
  };

  const renderJsonUpload = () => {
    if (jsonFile && parsedJson) {
      return (
        <Box
          p="sm"
          style={{
            borderRadius: '12px',
            backgroundColor: 'var(--mantine-color-gray-1)',
            border: '1px solid var(--mantine-color-green-5)',
          }}
        >
          <Flex align="center" gap="xs">
            <IconFileText size={20} color="var(--mantine-color-green-7)" />
            <Stack gap={0}>
              <Text fw={FONT_WEIGHTS.medium} size="sm" c="gray.10">
                {jsonFile.name}
              </Text>
              <Text fw={FONT_WEIGHTS.regular} size="xs" c="gray.6">
                {parsedJson.sections.length} sections • {parsedJson.templateType}
              </Text>
            </Stack>
          </Flex>
        </Box>
      );
    }

    return (
      <Dropzone
        onDrop={handleJsonUpload}
        onReject={fileRejections => {
          fileRejections.forEach(fileRejection => {
            notifications.show({
              title: `Failed to upload ${fileRejection.file.name}`,
              message: fileRejection.errors.map(e => e.message).join('\n'),
              color: 'red',
            });
          });
        }}
        maxSize={10 * 1024 * 1024}
        maxFiles={1}
        accept={['application/json']}
        style={{
          border: '1px dashed var(--mantine-color-gray-4)',
          borderRadius: '12px',
          padding: '16px',
          minHeight: '80px',
          backgroundColor: 'var(--mantine-color-gray-0)',
        }}
      >
        <Flex align="center" justify="center" gap="xs" style={{ minHeight: '48px' }}>
          <IconUpload size={16} color="var(--mantine-color-gray-6)" />
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.6">
            Drop JSON file here or click to browse
          </Text>
        </Flex>
      </Dropzone>
    );
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Choose import method"
      description="Select how you want to create your template."
      continueText={selectedMethod === 'json' ? 'Create template' : 'Continue'}
      canGoForward={canGoForward}
      hideProgress={true}
      nextStep={handleNext}
      isSubmitting={isLoading}
    >
      {isLoading && progress && (
        <Alert variant="light" color="blue.5" radius="lg" mb="xs">
          <Group gap="sm">
            <Loader size="sm" />
            <Text>{progress}</Text>
          </Group>
        </Alert>
      )}

      <Stack gap="md">
        <MethodOption
          title="Upload documents"
          description="Upload a blank template and a completed example. We'll use these to understand the structure and generate AI instructions."
          icon={<IconUpload size={24} />}
          isSelected={selectedMethod === 'upload'}
          onClick={() => handleMethodSelect('upload')}
        />

        <MethodOption
          title="Import from JSON"
          description="Import a previously exported template configuration. Skips directly to editing section instructions."
          icon={<IconFileImport size={24} />}
          isSelected={selectedMethod === 'json'}
          onClick={() => handleMethodSelect('json')}
        />

        <MethodOption
          title="Import from Markdown (Legacy)"
          description="Import a legacy markdown template file. Sections are split by level 2 headings (##)."
          icon={<IconMarkdown size={24} />}
          isSelected={selectedMethod === 'markdown'}
          onClick={() => handleMethodSelect('markdown')}
        />

        {selectedMethod === 'markdown' && (
          <Section>
            <Flex direction="column" gap="xxxs" mb="sm">
              <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
                Legacy Markdown Template
              </Text>
              <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
                Upload a .md file with sections split by ## headings
              </Text>
            </Flex>
            {renderMarkdownUpload()}
          </Section>
        )}

        {selectedMethod === 'json' && (
          <Section>
            <Flex direction="column" gap="xxxs" mb="sm">
              <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
                JSON Template File
              </Text>
              <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
                Upload a JSON file exported from another template
              </Text>
            </Flex>
            {renderJsonUpload()}
          </Section>
        )}
      </Stack>
    </DrawerFlowScreenLayout>
  );
}

type MethodOptionProps = {
  title: string;
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
};

function MethodOption({ title, description, icon, isSelected, onClick }: MethodOptionProps) {
  return (
    <Flex
      direction="column"
      p="md"
      bd={
        isSelected
          ? '2px solid var(--mantine-color-blue-5)'
          : '1px solid var(--mantine-color-gray-2)'
      }
      style={{
        borderRadius: 12,
        cursor: 'pointer',
        backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : 'white',
      }}
      onClick={onClick}
    >
      <Flex gap="sm" align="flex-start">
        <Box c={isSelected ? 'blue.6' : 'gray.6'}>{icon}</Box>
        <Flex direction="column" gap="xxxs">
          <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {title}
          </Text>
          <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
            {description}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <Flex direction="column" bg="gray.0" style={{ borderRadius: 12, overflowX: 'hidden' }}>
      <Flex
        direction="column"
        gap="xs"
        p="md"
        bd="1px solid var(--mantine-color-gray-1)"
        style={{ borderRadius: 12, overflowX: 'hidden' }}
        bg="white"
      >
        {children}
      </Flex>
    </Flex>
  );
}
